import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getAccessToken } from "../lib/auth-token";
import { getPreviewToken } from "../lib/preview-session";

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const previewToken = getPreviewToken();
  if (previewToken) {
    headers["x-preview-token"] = previewToken;
    return headers;
  }
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

let fpPromise: Promise<any> | null = null;

function getFingerprintAgent() {
  if (!fpPromise) {
    fpPromise = import("@fingerprintjs/fingerprintjs").then((FingerprintJS) =>
      FingerprintJS.load()
    );
  }
  return fpPromise;
}

interface UsePageLoggerOptions {
  userEmail?: string | null;
  userType: "authenticated" | "preview";
}

export function usePageLogger({ userEmail, userType }: UsePageLoggerOptions) {
  const location = useLocation();
  const lastLoggedRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userEmail) return;

    const key = `${location.pathname}:${userEmail}`;
    if (lastLoggedRef.current === key) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      lastLoggedRef.current = key;
      const fullPath = location.pathname + (location.search || "");
      try {
        const agent = await getFingerprintAgent();
        const result = await agent.get();
        const deviceFingerprint = result.visitorId;

        fetch("/api/log-page-view", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            userEmail,
            userType,
            pagePath: fullPath,
            deviceFingerprint,
          }),
        }).catch(() => {});
      } catch {
        fetch("/api/log-page-view", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            userEmail,
            userType,
            pagePath: fullPath,
            deviceFingerprint: "unavailable",
          }),
        }).catch(() => {});
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [location.pathname, userEmail, userType]);
}
